import type { ThresholdSignatureScheme } from '@dynamic-labs-wallet/browser-wallet-client';
import { WalletProviderEnum } from '@dynamic-labs/sdk-api-core';

import { getDefaultClient } from '../../../client/defaultClient';
import { NotWaasWalletProviderError } from '../../../errors/NotWaasWalletProviderError';
import { NoWalletProviderFoundError } from '../../../errors/NoWalletProviderFoundError';
import type { Chain } from '../../chain';
import { refreshAuth } from '../../user/refreshAuth';
import { getWalletProviderByKey } from '../../wallets/getWalletProviderByKey';
import { formatWalletProviderKey } from '../../wallets/utils/formatWalletProviderKey';
import { DYNAMIC_WAAS_METADATA } from '../constants';
import { isWaasWalletProvider } from '../isWaasWalletProvider';
import { normalizeWalletRequirements } from '../normalizeWalletRequirements/normalizeWalletRequirements';
import type { WalletCreationRequirement } from '../waas.types';

type CreateWaasWalletAccountsParams = {
  /**
   * For which chains to create wallet accounts.
   * Accepts either an array of chain strings (legacy format) or an array of
   * WalletCreationRequirement objects for chain-specific configuration.
   *
   * For Bitcoin, you can specify the address type ('native_segwit' or 'taproot').
   * If not specified, defaults to 'native_segwit'.
   *
   * If a chain is passed multiple times, just as many wallet accounts will be created
   * for that chain.
   */
  chains: Chain[] | WalletCreationRequirement[];
  /**
   * The optional password to use for the wallet accounts.
   * It's used to encrypt the user shares.
   * The same password must be used for all waas wallet accounts created for the same user.
   */
  password?: string;
  /**
   * The threshold signature scheme to use for the wallet accounts.
   * If not provided, TWO_OF_TWO will be used.
   */
  thresholdSignatureScheme?: ThresholdSignatureScheme;
};

/**
 * Creates wallet accounts for the specified chains.
 *
 * This function creates wallet accounts for the specified chains.
 * If the same chain is passed multiple times, just as many wallet accounts will be created
 * for that chain.
 *
 * @example
 * ```ts
 * // Legacy format - will create wallet accounts for EVM and SOL, BTC defaults to native_segwit
 * await createWaasWalletAccounts({ chains: ['EVM', 'SOL', 'BTC'] });
 *
 * // New format with chain-specific config
 * await createWaasWalletAccounts({
 *   chains: [
 *     { chain: 'BTC', bitcoinConfig: { addressType: 'taproot' } },
 *     { chain: 'EVM' },
 *   ]
 * });
 *
 * // Create both BTC address types
 * await createWaasWalletAccounts({
 *   chains: [
 *     { chain: 'BTC', bitcoinConfig: { addressType: 'native_segwit' } },
 *     { chain: 'BTC', bitcoinConfig: { addressType: 'taproot' } },
 *   ]
 * });
 * ```
 *
 * @param params.chains - The chains to create wallet accounts for.
 * @param params.password - The optional password to use for the wallet accounts.
 * @param params.thresholdSignatureScheme - The threshold signature scheme to use for the wallet accounts. (Default: TWO_OF_TWO)
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves when all wallet accounts are created.
 */
export const createWaasWalletAccounts = async (
  {
    chains,
    password,
    thresholdSignatureScheme,
  }: CreateWaasWalletAccountsParams,
  client = getDefaultClient()
): Promise<void> => {
  const normalizedRequirements = normalizeWalletRequirements({ input: chains });

  // we need this right now so we don't try to create wallet accounts for chains
  // that we don't support Dynamic Waas yet in the JS SDK, like TRON
  const supportedChains = ['BTC', 'EVM', 'SOL', 'SUI'];

  const filteredRequirements = normalizedRequirements.filter((requirement) =>
    supportedChains.includes(requirement.chain)
  );

  if (filteredRequirements.length === 0) {
    return;
  }

  const walletProviderMap = new Map<
    string,
    ReturnType<typeof getWalletProviderByKey>
  >();

  for (const requirement of filteredRequirements) {
    const walletProviderKey = formatWalletProviderKey({
      chain: requirement.chain,
      displayName: DYNAMIC_WAAS_METADATA.displayName,
      walletProviderType: WalletProviderEnum.EmbeddedWallet,
    });

    if (!walletProviderMap.has(walletProviderKey)) {
      const walletProvider = getWalletProviderByKey(
        { walletProviderKey },
        client
      );

      if (!walletProvider) {
        throw new NoWalletProviderFoundError({
          walletProviderKey,
        });
      }

      if (!isWaasWalletProvider(walletProvider)) {
        throw new NotWaasWalletProviderError({
          walletProviderKey,
        });
      }

      walletProviderMap.set(walletProviderKey, walletProvider);
    }
  }

  await Promise.all(
    filteredRequirements.map((requirement) => {
      const walletProviderKey = formatWalletProviderKey({
        chain: requirement.chain,
        displayName: DYNAMIC_WAAS_METADATA.displayName,
        walletProviderType: WalletProviderEnum.EmbeddedWallet,
      });

      const walletProvider = walletProviderMap.get(walletProviderKey);

      if (!walletProvider || !isWaasWalletProvider(walletProvider)) {
        return Promise.resolve();
      }

      return walletProvider.createWalletAccount({
        bitcoinConfig: requirement.bitcoinConfig,
        password,
        thresholdSignatureScheme,
      });
    })
  );

  await refreshAuth(client);
};
